import React, {Component} from 'react';
import {Grid, Icon, Header} from 'semantic-ui-react';
import {Alert} from 'componentLibrary';
import _ from 'lodash';
import {FemaGoTable} from 'app-ui-componentlibrary';
import {convertDateForDisplay, convertUTCDateToLocalDateDisplay} from 'common/utils';
import {connect} from 'react-redux';
import ApiActions from 'go/actions/api';
import {
  FileTransferUtils,
  convertFileSize,
} from 'go/components/common/attachments/uploader/utils';
import REST from 'go/requests/rest';
import {safeMult} from 'common/utils';
import {downloadPresignedLink, usePresignedUrl} from 'common/utils';
import {produce} from 'immer';
import {produceAppliedDiff} from 'framework/collaboration/diff.utils';

export class UploadPresentational extends Component {
  constructor(props, context) {
    super(props, context);

    this.state = {
      filesSuccessful: this.props.filesSuccessful,
    };
  }

  componentDidUpdate(prevProps) {
    let {currentlyCollaborating, sortBy, sortOrder} = this.props;
    if (!_.isEqual(prevProps.filesSuccessful, this.props.filesSuccessful)) {
      if (currentlyCollaborating) {
        let filesUpdated = produceAppliedDiff(
          prevProps.filesSuccessful,
          this.props.filesSuccessful,
          this.state.filesSuccessful
        );
        sortBy = sortBy || 'uploadedDate';
        sortOrder = sortOrder || 'asc';
        filesUpdated = _.orderBy(filesUpdated, sortBy, sortOrder);
        this.setState((prevState) => ({
          filesSuccessful: filesUpdated,
        }));
      } else {
        this.setState({filesSuccessful: this.props.filesSuccessful});
      }
    }
  }

  downloadAttachmentLink = (resourceId, fileName) => {
    if (usePresignedUrl()) {
      return (
        <a
          href="#"
          onClick={() => {
            downloadPresignedLink(resourceId);
          }}
          title={`Download ${fileName}`}
        >
          {fileName}
        </a>
      );
    } else {
      const downloadUrl = REST.attachment.getAttachmentDownloadUrl(resourceId);
      return (
        <a href={downloadUrl} title={`Download ${fileName}`}>
          {fileName}
        </a>
      );
    }
  };

  renderErrors(messages) {
    return (
      <React.Fragment>
        {!!messages.length && (
          <Alert
            header="There was an error with the following attachments, please try uploading again."
            type="error"
          >
            {_.map(messages, (message, index) => (
              <p key={index}>
                <strong>{message.name}:</strong>
                {message.error}
              </p>
            ))}
          </Alert>
        )}
      </React.Fragment>
    );
  }

  renderFailed(files) {
    const hasErrors = _.filter(files, (file) => file.error !== undefined);
    const errorMessages = _.map(hasErrors, (file) => ({
      name: file.fileName,
      error: file.error,
    }));
    return (
      <Grid.Row>
        <Grid.Column width={16}>
          <h4>Failed to Upload:</h4>
          <p>
            <strong>
              {_.map(files, (file, index) => {
                return <React.Fragment key={index}>{file.fileName}</React.Fragment>;
              })}
            </strong>
          </p>
          {this.renderErrors(errorMessages)}
        </Grid.Column>
      </Grid.Row>
    );
  }

  renderFileInput() {
    const {acceptedFileTypes, chooseMulti, attachmentType} = this.props;

    return (
      <input
        type="file"
        onChange={(e) => {
          e.preventDefault();
          this.props.handleChange(e.target.files);
          e.target.value = '';
        }}
        disabled={this.props.disabled}
        className="attachment-input-file"
        id={`attachmentUploadInput-${attachmentType}`}
        title="Upload from your computer"
        multiple={chooseMulti}
        accept={acceptedFileTypes}
      />
    );
  }

  handleDescriptionChange = (index, res) => {
    const {name, value} = res;
    if (this.props.currentlyCollaborating) {
      this.setState((prevState) => ({
        filesSuccessful: produce(prevState.filesSuccessful, (draft) => {
          draft[index][name] = value;
        }),
      }));
    } else {
      let filesSuccessful = this.state.filesSuccessful;
      filesSuccessful[index][name] = value;
      this.setState({
        filesSuccessful,
      });
    }
  };

  handleFileSize(fileSize) {
    return convertFileSize(fileSize, 1024);
  }

  render() {
    const {
      filesFailed,
      attachmentType,
      acceptedFileTypes,
      errorMessages,
      header,
      handleDelete,
      hrefId,
      buttonMsg,
      hideDeleteforNumRequired,
      numRequired,
      hideAction,
      currentlyCollaborating,
    } = this.props;
    const {filesSuccessful} = currentlyCollaborating ? this.state : this.props;
    const acceptedTypesDescriptor = acceptedFileTypes.join(', ');

    return (
      <Grid className={`${!_.isEmpty(errorMessages) ? ' errorBorderLine' : ''}`}>
        <Grid.Row>
          <Grid.Column>
            <h4 id={hrefId}>{header}</h4>
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column>
            {this.renderFileInput()}
            <label
              tabIndex={0}
              htmlFor={`attachmentUploadInput-${attachmentType}`}
              title={buttonMsg ? buttonMsg : 'Upload from your computer'}
              className={`ui medium primary left floated button ${
                this.props.disabled ? 'disabled' : ''
              }`}
            >
              <Icon name="upload" />
              {buttonMsg ? buttonMsg : 'Upload from your computer'}
            </label>
          </Grid.Column>
        </Grid.Row>
        <Grid.Row />
        <Grid.Row>
          <Grid.Column>
            <FemaGoTable
              type="static"
              summary="Review attachments table"
              className="sortable attachmentTableHeader"
            >
              <FemaGoTable.Header>
                <FemaGoTable.Row>
                  <FemaGoTable.HeaderCell>Filename</FemaGoTable.HeaderCell>
                  <FemaGoTable.HeaderCell>Date uploaded</FemaGoTable.HeaderCell>
                  <FemaGoTable.HeaderCell>File size</FemaGoTable.HeaderCell>
                  <FemaGoTable.HeaderCell>Description</FemaGoTable.HeaderCell>
                  {!hideAction && <FemaGoTable.HeaderCell>Action</FemaGoTable.HeaderCell>}
                </FemaGoTable.Row>
              </FemaGoTable.Header>
              <FemaGoTable.Body>
                {_.map(filesSuccessful, (file, index) => {
                  const {
                    fileName,
                    fileSize,
                    uploadedDate,
                    resourceId,
                    description,
                    id: attachmentId,
                  } = file;

                  const removeAttachmentLink = (
                    <a
                      className="remove-attachment"
                      onClick={(e) => {
                        e.preventDefault();
                        handleDelete(resourceId);
                      }}
                      title={`Remove the ${fileName} attachment`}
                      href="#"
                    >
                      <Icon link name="close" color="red" />
                      Remove
                    </a>
                  );
                  const date = _.isNumber(uploadedDate)
                    ? convertDateForDisplay(new Date(uploadedDate * 1000), true)
                    : convertUTCDateToLocalDateDisplay(uploadedDate);
                  if (fileSize && uploadedDate) {
                    //This essentially covers render race conditions causing pieces to load asynchronously
                    return (
                      <FemaGoTable.Row
                        key={currentlyCollaborating ? attachmentId : index}
                      >
                        <FemaGoTable.TextCell width={5}>
                          <Icon name="check circle" color="green" />
                          {this.downloadAttachmentLink(resourceId, fileName)}
                        </FemaGoTable.TextCell>
                        <FemaGoTable.TextCell width={1}>{date}</FemaGoTable.TextCell>
                        <FemaGoTable.TextCell width={1}>
                          {this.handleFileSize(fileSize)}
                        </FemaGoTable.TextCell>
                        <FemaGoTable.InputCell
                          value={description}
                          maxLength={501}
                          id={file.fileName + index + '.description'}
                          name="description"
                          handleChange={(e, res) => {
                            this.handleDescriptionChange(index, res);
                          }}
                          handleBlur={() => {
                            this.props.updateDescription(
                              FileTransferUtils.createPayload(
                                this.state.filesSuccessful[index]
                              )
                            );
                          }}
                        />
                        {!hideAction && (
                          <FemaGoTable.TextCell width={3}>
                            {(!hideDeleteforNumRequired ||
                              filesSuccessful.length > numRequired) &&
                              removeAttachmentLink}
                          </FemaGoTable.TextCell>
                        )}
                      </FemaGoTable.Row>
                    );
                  }
                })}
              </FemaGoTable.Body>
            </FemaGoTable>
          </Grid.Column>
        </Grid.Row>
        {!!filesFailed.length && this.renderFailed(filesFailed)}
        {!_.isEmpty(errorMessages) && (
          <div className="errorMessages">{errorMessages}</div>
        )}
        <Grid.Row>
          <Grid.Column width={6} className="left floated left aligned">
            <strong>
              <p>Maximum File Size:</p>
            </strong>
            {this.props.fileSizeLimit
              ? convertFileSize(safeMult(this.props.fileSizeLimit, 1048576), 1024)
              : '1 GB'}
          </Grid.Column>
          <Grid.Column width={6} className="right floated right aligned">
            <strong>
              <p>Accepted File Types:</p>
            </strong>
            {acceptedTypesDescriptor}
          </Grid.Column>
        </Grid.Row>
      </Grid>
    );
  }
}

export default UploadPresentational;
